import React from "react"
import { Card, CardContent } from "../components/ui/card"

const TermsOfService = () => {
  return (
    <div className="container mx-auto px-4 py-10 space-y-8">

      {/* Hero / title */}
      <section className="text-center space-y-3">
        <h1 className="text-3xl md:text-5xl font-bold font-display bg-gradient-to-r from-[#FF6B00] via-[#FF9A3D] to-[#FFD700] bg-clip-text text-transparent drop-shadow-glow">
          CaseRTP Terms of Service
        </h1>
        <p className="text-blue-100/80 text-sm md:text-base">
          Effective Date: <span className="font-semibold text-white">2025-05-18</span>
        </p>
        <p className="text-blue-100/70 max-w-2xl mx-auto text-sm md:text-base">
          By using CaseRTP (the &quot;Platform&quot;), you agree to these Terms of Service
          (&quot;Terms&quot;). If you do not agree with these Terms, please do not use our
          services. CaseRTP reserves the right to update or modify these Terms at any time.
        </p>
      </section>

      {/* Main content card */}
      <Card className="!bg-[#1A1D21]/40 border-[#2A2D31] backdrop-blur-sm">
        <CardContent className="p-6 md:p-8">
          <div className="space-y-6 text-blue-100/80 text-sm md:text-base leading-relaxed">
            {/* 1. Introduction */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                1. Introduction
              </h2>
              <p>
                CaseRTP is an independent data and analytics platform that provides
                transparent, accurate, and comprehensive insights into casual gaming
                products and platforms. Our content is based on publicly available
                information, focusing on key performance indicators such as Return to
                Player (RTP) rates and product reliability.
              </p>
              <p>
                We do not offer or facilitate any form of casual gaming products, betting,
                or sweepstakes. Our goal is to deliver unbiased rankings and comparisons
                based solely on aggregated data and objective analysis.
              </p>
              <p>
                While we strive to maintain up-to-date and accurate information, we cannot
                guarantee the completeness or reliability of third-party data sources.
              </p>
              <p>
                Mentioning a third-party platform or product does not constitute
                endorsement or recommendation. Users engage with third-party platforms at
                their own discretion.
              </p>
              <p>
                Access to our services may be restricted in jurisdictions where such
                informational content is regulated or prohibited. We reserve the right to
                block or limit access based on geographic or legal constraints.
              </p>
            </section>

            {/* 2. Data Accuracy & Methodology */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                2. Data Accuracy &amp; Methodology
              </h2>
              <p>
                CaseRTP is dedicated to providing accurate, timely, and transparent data
                derived from extensive real interactions with casual gaming products
                across various third-party platforms.
              </p>

              <h3 className="font-semibold text-white">Data Collection</h3>
              <p>
                We compile data from multiple reliable and publicly accessible sources,
                combined with proprietary insights and aggregated user contributions. This
                comprehensive approach allows us to analyze a wide spectrum of casual
                gaming products.
              </p>

              <h3 className="font-semibold text-white">Update Frequency</h3>
              <p>
                Our data and statistics are refreshed regularly, with frequency depending
                on the availability and reliability of our sources.
              </p>

              <h3 className="font-semibold text-white">Sample Size</h3>
              <p>
                RTP percentages and product statistics are calculated only when a
                sufficient number of interactions have been recorded, ensuring
                statistically meaningful results.
              </p>

              <h3 className="font-semibold text-white">Limitations</h3>
              <p>
                Despite our efforts to ensure data accuracy, actual outcomes may differ
                due to the inherent randomness of casual gaming products, changes by
                third-party platforms without notice, and natural fluctuations in sample
                size.
              </p>

              <h3 className="font-semibold text-white">Data Privacy &amp; Security</h3>
              <p>
                We are committed to protecting the privacy and security of any
                user-submitted data and comply with applicable data protection laws.
              </p>

              <h3 className="font-semibold text-white">Third-Party Data</h3>
              <p>
                Some data originates from third-party platforms, and we cannot guarantee
                the accuracy or completeness of such information.
              </p>

              <h3 className="font-semibold text-white">Right to Modify</h3>
              <p>
                We reserve the right to update or change our data collection and analysis
                methods as necessary to maintain accuracy and compliance.
              </p>

              <h3 className="font-semibold text-white">No Guarantee</h3>
              <p>
                CaseRTP provides analytical insights based on current data, not
                predictions or guarantees of specific outcomes. The RTP values and related
                statistics are intended for informational purposes only and do not imply
                any promise of future results or profits. Users rely on all information at
                their own discretion and risk.
              </p>

              <h3 className="font-semibold text-white">User Responsibility</h3>
              <p>
                Users should independently verify any information before making decisions
                based on our data or rankings.
              </p>
            </section>

            {/* 3. Unbiased Rankings & Transparency */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                3. Unbiased Rankings &amp; Transparency
              </h2>
              <p>
                At CaseRTP, we are committed to offering transparent, objective, and
                data-driven rankings of platforms offering casual gaming products. Our
                evaluations are based on performance metrics such as Return to Player
                (RTP).
              </p>
              <p>
                We do not sell ranking positions or accept any form of compensation,
                sponsorship, or incentive that could influence our rankings. Any
                adjustments are made strictly through data analysis and statistical
                modeling — never through financial arrangements.
              </p>
              <p>
                To preserve the integrity of our platform, we maintain full separation
                between our data operations and any potential commercial discussions with
                third parties.
              </p>
              <p>
                Rankings reflect a snapshot based on current data and may change over time
                as new information becomes available. Our methodology and data sources are
                documented and auditable upon request by qualified regulatory or legal
                entities.
              </p>
            </section>

            {/* 4. Third-Party Sites Disclaimer */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                4. Third-Party Sites Disclaimer
              </h2>
              <p>
                CaseRTP may link to third-party platforms that offer casual gaming
                products. These sites are entirely independent from CaseRTP. We do not
                own, operate, or exercise control over their content, services,
                compliance, or user experience.
              </p>
              <p>
                While our platform may reference performance metrics from these sites, we
                do not guarantee the accuracy, legality, or reliability of any third-party
                offering. Your use of such platforms is at your own discretion and risk.
                CaseRTP assumes no responsibility for any outcome, transaction, or issue
                that may arise from interactions with third-party platforms.
              </p>
              <p>
                Our rankings are based strictly on available data and are never influenced
                by payments, promotional partnerships, or preferential treatment. If we
                enter into any form of partnership or affiliate relationship with a
                third-party site, such relationships will be transparently disclosed on
                relevant parts of the platform.
              </p>
              <p>
                Users are encouraged to review the terms and conditions of any third-party
                site before engaging with their services.
              </p>
            </section>

            {/* 5. Affiliate Disclosure */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                5. Affiliate Disclosure
              </h2>
              <p>
                CaseRTP participates in affiliate programs with some of the third-party
                platforms mentioned on our site. This means we may earn a commission if
                you click a link and complete a qualifying action — such as registering,
                depositing funds, or using the platform in specific ways.
              </p>
              <p>
                These affiliate relationships do not influence our data, rankings, or
                analysis. All product results, RTP statistics, and platform comparisons
                are driven solely by independently collected performance data, not by
                commercial interests.
              </p>
              <p>
                We are committed to full transparency. A general disclosure regarding
                potential affiliate relationships is displayed prominently on our
                homepage, even if individual affiliate links are not labeled throughout
                the site.
              </p>
            </section>

            {/* 6. User Responsibilities */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                6. User Responsibilities
              </h2>
              <p>By accessing and using CaseRTP, you agree to the following:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <span className="font-semibold text-white">Accuracy of Information:</span>{" "}
                  You will provide truthful, current, and complete information where
                  required (e.g., if submitting feedback, using interactive features, or
                  signing up for updates).
                </li>
                <li>
                  <span className="font-semibold text-white">Lawful Use:</span> You will
                  only use CaseRTP for lawful purposes and refrain from any activities
                  that are fraudulent, misleading, or illegal.
                </li>
                <li>
                  <span className="font-semibold text-white">No Interference:</span> You
                  will not attempt to disrupt, reverse-engineer, tamper with, or bypass
                  any security features or technical protections on the platform.
                </li>
                <li>
                  <span className="font-semibold text-white">Personal Accountability:</span>{" "}
                  You understand that you are solely responsible for how you use CaseRTP
                  and any third-party platforms accessed through links on our site.
                </li>
                <li>
                  <span className="font-semibold text-white">Jurisdiction Compliance:</span>{" "}
                  Users are responsible for ensuring their use of CaseRTP complies with
                  applicable local laws and regulations.
                </li>
                <li>
                  <span className="font-semibold text-white">Prohibited Activities:</span>{" "}
                  You may not use automated bots, scrapers, or exploit bugs to access or
                  manipulate data without authorization.
                </li>
              </ul>
            </section>

            {/* 7. Limitation of Liability */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                7. Limitation of Liability
              </h2>
              <p>
                CaseRTP provides data and rankings &quot;as is&quot; without warranties of
                any kind, either express or implied. We do not guarantee accuracy,
                completeness, or fitness for any particular purpose.
              </p>
              <p>
                Under no circumstances shall CaseRTP, its affiliates, partners, or
                employees be liable for any direct, indirect, incidental, consequential,
                or punitive damages arising from your use of the platform or reliance on
                any information provided.
              </p>
              <p>Use of CaseRTP and related third-party sites is at your own risk.</p>
            </section>

            {/* 8. Indemnification */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                8. Indemnification
              </h2>
              <p>
                You agree to indemnify, defend, and hold harmless CaseRTP and its
                officers, directors, employees, and agents from and against any claims,
                damages, liabilities, losses, or expenses (including reasonable attorneys’
                fees) arising out of or related to your violation of these Terms or
                misuse of the platform.
              </p>
            </section>

            {/* 9. Changes to the Terms */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                9. Changes to the Terms
              </h2>
              <p>
                CaseRTP reserves the right to modify, update, or amend these Terms of
                Service at any time without prior notice. Any changes will be effective
                immediately upon posting and reflected in the &quot;Effective Date&quot;
                listed at the top of this document.
              </p>
              <p>
                We encourage you to review this page regularly to stay informed of any
                updates. Your continued use of CaseRTP after changes constitutes your
                acceptance of the updated terms.
              </p>
            </section>

            {/* 10. Restrictions and Prohibited Activities */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                10. Restrictions and Prohibited Activities
              </h2>
              <p>
                You agree not to engage in any activities that violate applicable laws,
                circumvent geographical or legal restrictions, or compromise the integrity
                and security of CaseRTP. Prohibited actions include, but are not limited
                to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  Using VPNs, proxies, or other tools to bypass geographic or regulatory
                  restrictions imposed by CaseRTP.
                </li>
                <li>
                  Attempting to interfere with, hack, disrupt, or compromise the security
                  or functionality of the platform or its users.
                </li>
                <li>
                  Engaging in fraudulent, deceptive, or abusive behavior toward CaseRTP,
                  its users, or third-party platforms featured on the site.
                </li>
                <li>
                  Automating access or data extraction through unauthorized means (bots,
                  scripts, scraping) that violate our policies.
                </li>
              </ul>
              <p>
                Violation of these restrictions may result in suspension or termination of
                your access to CaseRTP, as well as potential legal action.
              </p>

              <h3 className="font-semibold text-white">Enforcement Rights</h3>
              <p>
                CaseRTP reserves the right to monitor compliance and enforce these
                restrictions through technical and legal means as necessary.
              </p>

              <h3 className="font-semibold text-white">Reporting Abuse</h3>
              <p>
                Users are encouraged to report any suspected abuse or security issues to
                CaseRTP promptly.
              </p>
            </section>

            {/* 11. Contact Us */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                11. Contact Us
              </h2>
              <p>
                If you have any questions, concerns, or need assistance regarding these
                Terms or our services, please don&apos;t hesitate to contact us at:
              </p>
              <p>
                <span className="font-semibold text-white">Email:</span> CaseRTP.team@gmail.com
              </p>
            </section>

            {/* 12. Governing Law and Jurisdiction */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                12. Governing Law and Jurisdiction
              </h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws
                of Sweden.
              </p>
              <p>
                Any disputes arising out of or relating to these Terms or the use of
                CaseRTP shall be subject to the exclusive jurisdiction of the courts of
                Sweden, with Malmö District Court as the first instance, unless otherwise
                required by mandatory law.
              </p>
            </section>

            {/* 13. Intellectual Property Rights */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                13. Intellectual Property Rights
              </h2>
              <p>
                All content, trademarks, logos, and data on CaseRTP are the property of
                CaseRTP or its licensors. You agree not to reproduce, distribute, or
                create derivative works without explicit permission.
              </p>
            </section>

            {/* 14. Termination */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                14. Termination
              </h2>
              <p>
                CaseRTP reserves the right to suspend or terminate your access to the
                platform at any time, without notice, for violation of these Terms or any
                other reason deemed necessary to protect the service and its users.
              </p>
            </section>

            {/* 15. Force Majeure */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                15. Force Majeure
              </h2>
              <p>
                CaseRTP shall not be held liable for any failure or delay in performance
                due to causes beyond our reasonable control, including but not limited to
                natural disasters, acts of government, internet outages, or cyber-attacks.
              </p>
            </section>

            {/* 16. User-Generated Content Policy */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                16. User-Generated Content Policy
              </h2>
              <p>
                If users submit reviews or data, they grant CaseRTP a worldwide,
                royalty-free license to use, modify, and display such content. Users must
                ensure content is lawful and not infringing on third-party rights.
                CaseRTP reserves the right to remove any content at its discretion.
              </p>
            </section>

            {/* 17. Dispute Resolution & Arbitration */}
            <section className="space-y-2">
              <h2 className="text-xl font-semibold font-display text-white">
                17. Dispute Resolution &amp; Arbitration
              </h2>
              <p>
                If a dispute arises out of or relates to these Terms or your use of
                CaseRTP, we encourage you to contact us first so we can attempt to resolve
                it informally.
              </p>
              <p>
                For business users (B2B), any unresolved disputes shall be finally settled
                by binding arbitration administered by the Arbitration Institute of the
                Stockholm Chamber of Commerce (SCC). The seat of arbitration shall be
                Stockholm, Sweden. The language of the proceedings shall be English. The
                arbitral award shall be final and binding.
              </p>
              <p>
                For individual users (B2C), if you reside in a country where consumer
                protection laws prohibit pre-dispute arbitration agreements, you retain
                the right to resolve any disputes in the public courts of your country of
                residence. If you voluntarily choose arbitration after a dispute arises, we
                will comply with that request.
              </p>
              <p>
                In all cases, these Terms shall be governed by the laws of Sweden, without
                regard to conflict-of-law principles.
              </p>
            </section>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default TermsOfService
